import React, { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase/config';
import { ensureHttps } from '../../utils/urlUtils';

export default function SignatureCapture({ open, onClose, onSave, estimateId, existingSignature }) {
  const sigPadRef = useRef(null);
  const [signedByName, setSignedByName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const handleClear = () => {
    if (sigPadRef.current) {
      sigPadRef.current.clear();
      setError(null);
    }
  };

  const handleSave = async () => {
    if (!sigPadRef.current || sigPadRef.current.isEmpty()) {
      setError('Please provide a signature before saving.');
      return;
    }

    if (!signedByName.trim()) {
      setError('Please enter your name.');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      // Get signature as data URL
      const signatureDataUrl = sigPadRef.current.toDataURL('image/png');
      
      let signatureUrl;
      
      // Try to upload to Firebase Storage first
      // For public portals, we'll fall back to base64 if Storage fails
      let storageUploadAttempted = false;
      try {
        // Convert data URL to Blob for more reliable upload
        const response = await fetch(signatureDataUrl);
        const blob = await response.blob();
        
        console.log('Signature converted to blob, size:', blob.size, 'bytes');

        // Upload to Firebase Storage
        const timestamp = Date.now();
        const fileName = `signature_${timestamp}.png`;
        const signatureRef = ref(storage, `estimates/${estimateId}/${fileName}`);

        console.log('Attempting to upload signature to Firebase Storage...');
        console.log('Estimate ID:', estimateId);
        console.log('Storage path:', `estimates/${estimateId}/${fileName}`);
        
        storageUploadAttempted = true;
        
        // Upload as Blob (more reliable than data URL)
        // Use a timeout to avoid hanging on CORS errors
        const uploadPromise = uploadBytes(signatureRef, blob, {
          contentType: 'image/png'
        });
        
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Upload timeout')), 5000)
        );
        
        await Promise.race([uploadPromise, timeoutPromise]);
        console.log('Signature uploaded successfully to Storage');

        // Get download URL
        const downloadURL = await getDownloadURL(signatureRef);
        console.log('Download URL obtained:', downloadURL);
        
        // Normalize to HTTPS
        signatureUrl = ensureHttps(downloadURL);
      } catch (storageError) {
        // If Storage upload fails (e.g., CORS issue, timeout, network error), save as base64 data URL in Firestore
        console.warn('Firebase Storage upload failed or timed out, saving as base64 in Firestore:', storageError);
        console.log('This is acceptable - signature will be stored directly in Firestore as base64 data URL');
        // Use data URL directly - Firestore supports base64 data URLs for images
        signatureUrl = signatureDataUrl;
        
        // Suppress the error - this is an expected fallback
        if (storageError.message !== 'Upload timeout') {
          // Only log non-timeout errors
          console.warn('Storage error details (non-critical):', storageError.message);
        }
      }

      // Call onSave with signature data (either Storage URL or base64 data URL)
      console.log('Calling onSave with signature data...');
      console.log('Signature URL type:', signatureUrl.startsWith('data:') ? 'base64 data URL' : 'Storage URL');
      await onSave({
        signatureUrl: signatureUrl,
        signedAt: new Date().toISOString(),
        signedByName: signedByName.trim()
      });
      console.log('onSave completed successfully');

      // Reset form
      if (sigPadRef.current) {
        sigPadRef.current.clear();
      }
      setSignedByName('');
      setUploading(false);
      
      // Close dialog after a brief delay to show success
      setTimeout(() => {
        onClose();
      }, 100);
    } catch (err) {
      console.error('Error saving signature:', err);
      console.error('Error details:', {
        message: err.message,
        code: err.code,
        stack: err.stack
      });
      
      // Show error message
      setError(`Failed to save signature: ${err.message || 'Please check the console and try again.'}`);
      setUploading(false);
      
      // Don't close the dialog on error so user can try again
    }
  };

  const handleClose = () => {
    if (sigPadRef.current && !sigPadRef.current.isEmpty()) {
      if (!window.confirm('Are you sure you want to close? Your signature will be lost.')) {
        return;
      }
    }
    setError(null);
    setSignedByName('');
    if (sigPadRef.current) {
      sigPadRef.current.clear();
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Sign Estimate</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {uploading && (
            <Alert severity="info" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              Uploading signature and saving to database...
            </Alert>
          )}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Please sign in the box below:
            </Typography>
            <Box
              sx={{
                border: '2px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'background.paper',
                p: 1,
                mb: 2
              }}
            >
              <SignatureCanvas
                ref={sigPadRef}
                canvasProps={{
                  width: 500,
                  height: 200,
                  className: 'signature-canvas',
                  style: {
                    width: '100%',
                    maxWidth: '500px',
                    height: '200px',
                    cursor: 'crosshair'
                  }
                }}
                backgroundColor="white"
                penColor="#000000"
              />
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={handleClear}
              disabled={uploading}
            >
              Clear
            </Button>
          </Box>

          <TextField
            fullWidth
            label="Name (required)"
            value={signedByName}
            onChange={(e) => setSignedByName(e.target.value)}
            placeholder="Enter your full name"
            required
            disabled={uploading}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={uploading || !signedByName.trim()}
        >
          {uploading ? 'Saving...' : 'Save Signature'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

